export type CollectionDataType = 'structured' | 'unstructured';

export type CollectionSource = 'uploads' | 'wordpress_api';

export interface CollectionSourceAuth {
  username?: string | null;
  password?: string | null;
  has_credentials?: boolean;
  ssm_parameter_arn?: string | null;
}

export interface WordPressSiteConfig {
  site_id: string;
  label: string;
  base_url: string;
  schedule_cron: string;
  cron_enabled: boolean;
  auth?: CollectionSourceAuth | null;
  wordpress_post_types?: string[] | null;
  wordpress_per_page?: number | null;
  wordpress_exclusion_rules?: WordPressExclusionRule[] | null;
  /**
   * Custom payload field to use as the ingested content body for WordPress
   * users (authors). WordPress's standard user schema only exposes the short
   * `description`; sites that register a custom field (e.g. `long_description`)
   * name it here. Falls back to `description` when unset.
   */
  user_content_field?: string | null;
  last_ingested_at?: string | null;
  ingestion_status?: 'idle' | 'running';
  ingestion_task_id?: string | null;
}

export interface CollectionSourceConfig {
  wordpress_sites?: WordPressSiteConfig[] | null;

  /**
   * Collection-level opt-in for the nightly deletion-reconcile job. When true,
   * qdrant-svc sweeps Qdrant against live WordPress content nightly and deletes
   * points for posts removed/unpublished upstream. Defaults false. Gated
   * per-collection (not per-site) so the orphan-site sweep stays correct.
   */
  reconcile_enabled?: boolean;

  // Legacy fields (read-only, for backward compat)
  base_url?: string | null;
  schedule_cron?: string | null;
  cron_enabled?: boolean | null;
  auth?: CollectionSourceAuth | null;
  wordpress_per_page?: number | null;
  wordpress_post_types?: string[] | null;
  wordpress_exclusion_rules?: WordPressExclusionRule[] | null;
  user_content_field?: string | null;
}

export type WordPressContentRuleMode = 'include' | 'exclude';

/**
 * Ad-hoc top-level key/value condition. Matches when the post/user's
 * top-level `key` stringifies (case-insensitive) to `value`. Mirrors the
 * qdrant-svc `FieldMatch` and platform-api `WordPressFieldMatch` models.
 */
export interface WordPressFieldMatch {
  key: string;
  value: string;
}

/**
 * WordPress ingestion filter rule. Historically exclusion-only; now supports
 * allow-list via `mode: 'include'`. DDB key `wordpress_exclusion_rules` is
 * preserved for backward compatibility with previously persisted configs.
 */
export interface WordPressExclusionRule {
  name: string;
  /**
   * Filter mode. Defaults to 'exclude' when absent (BC for persisted rows).
   * - 'exclude': matching posts are dropped.
   * - 'include': allow-list. If any include rule exists, posts must match
   *   ≥1 include rule to be ingested.
   */
  mode?: WordPressContentRuleMode;
  title_contains?: string[] | null;
  tags?: string[] | null;
  categories?: string[] | null;
  /**
   * Ad-hoc top-level key/value conditions (e.g. custom attributes on users).
   * OR logic: the rule matches if any pair matches.
   */
  field_matches?: WordPressFieldMatch[] | null;
  /**
   * Optional human-readable note. May be absent or null on rules returned
   * by the API (paired platform-api change made reason optional). Always
   * guard with `rule.reason ?? ''` before calling string methods.
   */
  reason?: string | null;
}

export interface StructuredUpload {
  excel_object_key: string;
  sheet_count: number;
  sheets: {
    name: string;
    index: number;
  }[];
  requires_sheet_selection: boolean;
  converted_csv_object_key?: string | null;
}

export interface DatasetCsvForeignKeyReference {
  dataset_id?: string;
  table?: string;
  columns?: string[];
  [key: string]: unknown;
}

export interface DatasetCsvForeignKey {
  columns?: string[];
  references?: DatasetCsvForeignKeyReference;
  [key: string]: unknown;
}

export interface DatasetCsvReference {
  s3_key: string;
  primary_key?: string[];
  foreign_keys?: DatasetCsvForeignKey[];
  [key: string]: unknown;
}

export interface DatasetCsvUploadMetadata {
  headers?: string[];
  primary_key?: string[];
  primary_key_suggestions?: string[];
  suggested_primary_keys?: string[];
  foreign_keys?: DatasetCsvForeignKey[];
  quality?: CSVQuality;
  [key: string]: unknown;
}

export type QualityAction = 'rename' | 'drop' | 'fill';

export interface QualityResolution {
  action: QualityAction;
  value?: string;
  sanitized_value?: string;
  original_header?: string;
  updated_at?: string;
}

export interface QualityIssue {
  type: 'EMPTY_HEADER' | 'EMPTY_COLUMN' | 'DUPLICATE_HEADER' | string;
  column_index: number;
  status: 'unresolved' | 'resolved' | 'ignored' | string;
  suggested_actions?: QualityAction[];
  message?: string;
  resolution?: QualityResolution;
}

export interface CSVQuality {
  requires_resolution: boolean;
  issues: QualityIssue[];
  resolutions: Record<string, QualityResolution>;
  dropped_columns: Array<{
    column_index: number;
    original_header: string;
    sanitized_header?: string;
  }>;
  applied_at?: string;
  issue_count?: number;
  headers?: string[];
}

export interface DatasetTableMetadata {
  table?: string;
  filename?: string;
  s3_key?: string;
  source_key?: string;
  csv_key?: string;
  columns?: string[];
  column_name_mapping?: Record<string, unknown>;
  quality?: CSVQuality;
  [key: string]: unknown;
}

export interface DatasetMetadataResponse {
  tables: DatasetTableMetadata[];
}

export interface Dataset {
  id: string;
  name: string;
  description?: string;
  csv_object_keys: DatasetCsvReference[];
  created_at: string;
  updated_at: string;
}

export interface Source {
  filename: string;
  size_mb?: number | null;
  original_filename: string;
  s3_url: string;
  content_type?: string | null; // Backend returns MIME type strings (e.g., "application/csv", "application/json")
  key?: string | null;
  last_modified?: string | null;
  metadata?: SourceMetadata | null;
  size?: number | null;
  upload_timestamp?: string | null;
  tags?: {
    indexing_status?: string;
    ingestion_method?: 'celery' | 'lambda';
    external_id?: string;
    [key: string]: string | undefined;
  };
  quality?: CSVQuality | null;
  // Celery ingestion tracking fields
  celery_task_id?: string;
  ingestion_method?: 'celery' | 'lambda';
  ingestion_status?: string;
  celery_task_status?: 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE';
  celery_status_last_checked?: number;
}

export interface SourceMetadata {
  'file-size-bytes': string;
  'original-filename': string;
  'upload-timestamp': string;
}

// TODO: make this implementations dynamic from backend and more flexible
export enum SourceType {
  RICH_TEXT = 'text/rtf',
  TEXT = 'text/plain',
  POWERPOINT = 'application/vnd.ms-powerpoint',
  POWERPOINTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  MSWORD = 'application/msword',
  MSWORDX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  PDF = 'application/pdf',
  JSON = 'application/json',
  CSV = 'text/csv',
  TSV = 'text/tab-separated-values',
  EXCEL = 'application/vnd.ms-excel',
  SHEET = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  NDJSON = 'application/x-ndjson',
  MARKDOWN = 'text/markdown',
  PYTHON = 'text/x-python',
  JAVASCRIPT = 'text/javascript',
  TYPESCRIPT = 'text/typescript',
  SHELL = 'text/x-shellscript',
  R = 'text/x-r',
  SQL = 'text/x-sql',
  ZIP = 'application/zip',
  ZIP_COMPRESSED = 'application/x-zip-compressed',
  GZIP = 'application/gzip',
  GZIP_X = 'application/x-gzip',
  /** Gzipped tarball; only ever reported for .tar.gz/.tgz, never bare .gz */
  COMPRESSED_TAR = 'application/x-compressed-tar',
  /** Bundled skill packages; use a private MIME type so octet-stream doesn't whitelist all binaries */
  SKILL = 'application/x-skill',
  PARQUET = 'application/vnd.apache.parquet',
  AUDIO_MPEG = 'audio/mpeg',
  AUDIO_WAV = 'audio/wav',
  AUDIO_MP4 = 'audio/mp4',
  VIDEO_MP4 = 'video/mp4',
  VIDEO_MOV = 'video/quicktime',
  VIDEO_WEBM = 'video/webm',
  PNG = 'image/png',
  JPEG = 'image/jpeg',
  GIF = 'image/gif',
  WEBP = 'image/webp',
}

export type SourceOrderType =
  | 'name_asc'
  | 'name_desc'
  | 'upload_asc'
  | 'upload_desc'
  | 'size_asc'
  | 'size_desc';

export enum SourceOrder {
  NAME_ASC = 'name_asc',
  NAME_DESC = 'name_desc',
  DATE_ASC = 'upload_asc',
  DATE_DESC = 'upload_desc',
  SIZE_ASC = 'size_asc',
  SIZE_DESC = 'size_desc',
}

export type CollectionOrderType =
  | 'name_asc'
  | 'name_desc'
  | 'created_asc'
  | 'created_desc';

export enum CollectionOrder {
  NAME_ASC = 'name_asc',
  NAME_DESC = 'name_desc',
  CREATED_ASC = 'created_asc',
  CREATED_DESC = 'created_desc',
}

export interface CollectionConfig {
  embedding_dimension: string;
  cluster_id: string | null;
  distance_metric: string;
  collection_name: string;
  shard_count?: string;
  datasets?: Dataset[];
  chat_system_prompt?: string | null;
  chat_filters?: Array<{
    keywords: string[];
    key: string;
    value: unknown;
  }>;
  chat_prompts?: Array<{
    text: string;
    chat_mode: string;
  }>; // Saved chat prompts with chat mode
}

export interface Collection {
  orgid: string;
  name: string;
  description: string;
  destination: string;
  s3_prefix: string;
  config: CollectionConfig;
  id: string;
  created_at: string;
  updated_at: string;
  status: string;
  deletion_requested_at: string;
  data_type?: CollectionDataType;
  source?: CollectionSource;
  source_config?: CollectionSourceConfig | null;
  external_chat_agent_id?: string | null;
  external_chat_config?: Record<string, unknown> | null;
  user_role?: 'owner' | 'editor' | 'viewer' | null;
  owner_email?: string | null;
  exposed_upload_attributes?: string[] | null;
  exposed_upload_attribute_cardinality?: Record<
    string,
    'single' | 'multi'
  > | null;
  external_chat_filters?: ExternalChatFilterDef[] | null;
  // Platform-provisioned collections; these can't be deleted or renamed.
  is_system_managed?: boolean;
  /** Discriminator for system collections (e.g. 'sources'). */
  system_type?: string | null;
  /**
   * Owning user's email for a per-user system collection (the personal
   * Sources collection). The list API only ever returns the requesting
   * user's own, so finding by system_type is sufficient client-side.
   */
  system_owner?: string | null;
}

export type ExternalChatFilterType =
  | 'select'
  | 'multiselect'
  | 'text'
  | 'range';

export interface ExternalChatFilterDef {
  field: string;
  label: string;
  type: ExternalChatFilterType;
  /**
   * Per-filter cardinality. Required to be 'multi' when type is
   * 'multiselect'. May be null or 'single' for select/text/range.
   */
  cardinality?: 'single' | 'multi' | null;
  values?: string[] | null;
  order?: number;
  description?: string | null;
}

// File extension arrays for icon mapping (derived from AcceptType categories)
// and for classifying artifacts (see utils/artifact-type-label).
export const SPREADSHEET_EXTENSIONS = [
  'xls',
  'xlsx',
  'xlsm',
  'xlsb',
  'ods',
  'csv',
  'tsv',
  'parquet',
];
export const PRESENTATION_EXTENSIONS = ['ppt', 'pptx'];
export const DOCUMENT_EXTENSIONS = ['doc', 'docx'];
// OpenDocument and macro-enabled variants: recognized for icon and type
// classification, but kept out of PRESENTATION_EXTENSIONS/DOCUMENT_EXTENSIONS
// because artifact-file-renderer's Office viewer can't render ODF.
export const PRESENTATION_EXTENSIONS_EXTRA = ['pptm', 'odp'];
export const DOCUMENT_EXTENSIONS_EXTRA = ['docm', 'odt'];
export const TEXT_EXTENSIONS = ['txt', 'rtf', 'md', 'markdown'];
export const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];
export const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'avi', 'mkv', 'm4v'];
export const AUDIO_EXTENSIONS = ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac'];
